import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  TreePine,
  Leaf,
  Droplets,
  Flower2,
  Scissors,
  Mountain,
  Sun,
  Shovel,
  ArrowRight,
  CheckCircle,
} from "lucide-react";

const ServicesPage = () => {
  const allServices = [
    {
      icon: TreePine,
      title: "Landscape Design",
      description:
        "Custom landscape designs tailored to your property, lifestyle, and budget. We create outdoor spaces that are both beautiful and functional.",
      features: [
        "Initial consultation and site assessment",
        "Custom design plans and 3D renderings",
        "Plant selection guidance",
        "Project timeline and budget planning",
      ],
    },
    {
      icon: Flower2,
      title: "Lawn Installation",
      description:
        "Professional lawn installation services including sod and seed. We prepare the soil, install your new lawn, and provide care instructions.",
      features: [
        "Soil testing and preparation",
        "High-quality sod or seed selection",
        "Professional installation",
        "Post-installation care guide",
      ],
    },
    {
      icon: Leaf,
      title: "Garden Planting",
      description:
        "Expert garden planting services for flowers, shrubs, trees, and ornamental plants. We help you choose the right plants for your climate and soil.",
      features: [
        "Plant selection consultation",
        "Proper planting techniques",
        "Mulching and initial care",
        "Seasonal planting schedules",
      ],
    },
    {
      icon: Scissors,
      title: "Lawn Mowing & Care",
      description:
        "Regular lawn mowing and maintenance services to keep your lawn healthy and well-manicured throughout the growing season.",
      features: [
        "Scheduled mowing service",
        "Edging and trimming",
        "Lawn health monitoring",
        "Seasonal adjustments",
      ],
    },
    {
      icon: Sun,
      title: "Hedge & Shrub Trimming",
      description:
        "Professional hedge and shrub trimming to maintain shape, promote healthy growth, and enhance your property's curb appeal.",
      features: [
        "Regular maintenance trimming",
        "Shaping and sculpting",
        "Disease inspection",
        "Clean-up included",
      ],
    },
    {
      icon: Mountain,
      title: "Seasonal Cleanup",
      description:
        "Comprehensive spring and fall cleanup services to prepare your landscape for the changing seasons.",
      features: [
        "Leaf removal and disposal",
        "Bed preparation",
        "Debris clearing",
        "Winterization services",
      ],
    },
    {
      icon: Droplets,
      title: "Irrigation Setup",
      description:
        "Efficient irrigation system design and installation to keep your landscape properly watered while conserving water.",
      features: [
        "System design and planning",
        "Professional installation",
        "Smart controller options",
        "Maintenance and repairs",
      ],
    },
    {
      icon: Shovel,
      title: "Mulching & Soil Prep",
      description:
        "Mulching services and soil preparation to improve plant health, retain moisture, and suppress weeds.",
      features: [
        "Quality mulch selection",
        "Proper mulch application",
        "Soil testing and amendment",
        "Composting options",
      ],
    },
  ];

  return (
    <div data-testid="services-page" className="min-h-screen bg-[#FAFAF8]">
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-[#2D5A27] py-20 md:py-28">
          <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1
                data-testid="services-page-headline"
                className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold mb-6"
              >
                Our Services
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed">
                Comprehensive landscaping and gardening services for residential homeowners in Los Angeles. From design to maintenance, we've got you covered.
              </p>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {allServices.map((service, index) => (
                <Card
                  key={index}
                  data-testid={`service-detail-${index}`}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 bg-[#F3F4F1] rounded-xl flex items-center justify-center flex-shrink-0">
                        <service.icon className="w-7 h-7 text-[#2D5A27]" />
                      </div>
                      <div>
                        <h3 className="font-heading text-xl font-semibold text-[#1A2C22] mb-2">
                          {service.title}
                        </h3>
                        <p className="text-[#4B5563] text-sm leading-relaxed">
                          {service.description}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {service.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-3">
                          <CheckCircle className="w-4 h-4 text-[#2D5A27] flex-shrink-0 mt-0.5" />
                          <span className="text-[#4B5563] text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pricing Note */}
            <div className="mt-12 text-center">
              <p className="text-[#6B7280] text-sm bg-white inline-block px-6 py-3 rounded-full shadow-sm">
                Service pricing varies depending on property size, materials, and project scope.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#1A2C22] py-16 md:py-20">
          <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8 text-center">
            <h2 className="font-heading text-3xl md:text-4xl text-white font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Contact us today for a free estimate. We'll assess your property and provide a detailed quote.
            </p>
            <Link to="/contact">
              <Button
                data-testid="services-cta"
                className="bg-[#C2410C] hover:bg-[#9A3412] text-white h-14 px-10 text-lg rounded-full"
              >
                Get Free Estimate
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServicesPage;
